import {useNavigate} from 'react-router-dom';

import {
	EmergencyTelehealthEnum,
	FeaturesEnum,
	MyAppointmentsEnum,
	TermsOriginEnum,
} from 'presentation/constants';
import {useGetEndpointUrl, useHealthEmergencyLinkFactory} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useServicePermission} from 'presentation/hooks/useServicePermission';
import {BookingTypeEnum} from 'presentation/pages/private/booking/constants';
import {useBookingConfirmationDrawer} from 'presentation/pages/private/booking/hooks/useBookingConfirmationDrawer';
import {useVoiceTranscriptionDrawerStore} from 'presentation/components/VoiceTranscriptionDrawer/store';
import {useOpenOptionsDrawerStore} from 'presentation/pages/private/store/useOpenOptionsDrawer';
import {ServicesTypesEnum} from 'presentation/pages/private/services/types';

import {OptionsDrawerItem} from '../types';
import {translations} from 'presentation/translations-new';

const {bookTelehealth, startEmergencyTelehealth, seeMyAppointments} =
	translations['pt-br'].privateFlow.services;
const {emergency} = translations['pt-br'].commons;

function useTelemedicineItems() {
	const navigate = useNavigate();
	const {canOpenService} = useServicePermission();
	const {setSelectedServiceType, handleOpenBookingConfirmationDrawer} =
		useBookingConfirmationDrawer();
	const onOpenVoiceTranscriptionDrawer = useVoiceTranscriptionDrawerStore(state => state.onOpen);
	const closeOptionsDrawer = useOpenOptionsDrawerStore(state => state.closeOptionsDrawer);
	const {isEnabled: isUrgencyTeleTermsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_URGENCY_TELE_TERMS,
	);
	const {isEnabled: isLandingPageTeleEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_LANDING_PAGE_TELE,
	);
	const {isError, setIsError, getLink} = useGetEndpointUrl(emergency);
	const makeHealthEmergencyLink = useHealthEmergencyLinkFactory();

	const handleEmergencyTelehealthClick = () => {
		const canAccessService = canOpenService(ServicesTypesEnum.EMERGENCY_TELEHEALTH);

		if (!canAccessService) {
			closeOptionsDrawer();
			return;
		}
		closeOptionsDrawer();

		if (isLandingPageTeleEnabled) {
			navigate(EmergencyTelehealthEnum.EMERGENCY_TELEHEALTH_FLOW);
			return;
		}

		if (isUrgencyTeleTermsEnabled) {
			onOpenVoiceTranscriptionDrawer(null, TermsOriginEnum.EMERGENCY_TELEHEALTH);
			return;
		}

		getLink(makeHealthEmergencyLink);
	};

	const handleBookTelehealthClick = () => {
		const canAccessService = canOpenService(ServicesTypesEnum.TELEHEALTH);

		if (!canAccessService) {
			closeOptionsDrawer();
			return;
		}

		closeOptionsDrawer();
		setSelectedServiceType(BookingTypeEnum.TELEHEALTH);
		handleOpenBookingConfirmationDrawer();
	};

	const handleMyAppointmentsClick = () => {
		closeOptionsDrawer();
		navigate(MyAppointmentsEnum.MY_APPOINTMENTS);
	};

	const items: OptionsDrawerItem[] = [
		{
			label: startEmergencyTelehealth,
			icon: 'video-default',
			onClick: handleEmergencyTelehealthClick,
		},
		{
			label: bookTelehealth,
			icon: 'calendar-add-filled',
			onClick: handleBookTelehealthClick,
		},
		{
			label: seeMyAppointments,
			icon: 'event-filled',
			onClick: handleMyAppointmentsClick,
		},
	];

	return {items, isError, setIsError, handleEmergencyTelehealthClick};
}

export {useTelemedicineItems};
